import { useNavigate, useSearchParams } from "react-router-dom";
import useRoles from "../../features/Roles/hooks/useRoles";
import { useEffect, useState } from "react";
import { 
  Button, 
  Center, 
  Flex, 
  Heading, 
  VStack, 
  HStack,
  Box,
  Text,
  Spinner,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Image,
  Input,
  Select
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { ROUTES } from "../../consts/routes";
import { OlaggModule, PermissionEnum } from "@olagg/db-types";
import Pagination from "@olagg/ui-kit/Pagination";
import useLevelRewards from "./hooks/useLevelRewards";
import { AdminLevelReward } from "@olagg/db-types";

const Actions = ({ levelReward, onLevelRewardDeleted }: { levelReward: AdminLevelReward; onLevelRewardDeleted: () => void; }) => {
  const { missingPermission } = useRoles();
  const { deleteLevelReward } = useLevelRewards();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  
  const handleViewLevelReward = () => navigate(`/level-rewards/${levelReward.id}`);

  const handleRemoveLevelReward = async () => {
    if (window.confirm('¿Estás seguro que deseas eliminar este premio de nivel?')) {
      setIsDeleting(true);
      try {
        await deleteLevelReward(levelReward.id.toString());
        await onLevelRewardDeleted();
      } catch (error) {
        console.error("Error eliminando premio de nivel:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  }

  const canEdit = !missingPermission(OlaggModule.LevelReward, PermissionEnum.EDIT);
  const canDelete = !missingPermission(OlaggModule.LevelReward, PermissionEnum.DELETE);

  return (
    <Box display="flex" gap="10px" flexWrap="wrap">
      <Button 
        isDisabled={!canEdit} 
        colorScheme="green"
        size="sm"
        onClick={handleViewLevelReward}
      >
        Ver
      </Button>
      <Button 
        isDisabled={!canDelete || isDeleting} 
        colorScheme="red"
        size="sm"
        onClick={handleRemoveLevelReward}
        isLoading={isDeleting}
        loadingText="Eliminando..."
      >
        Eliminar
      </Button>
    </Box>
  )
}

const LevelRewardsList = () => {
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams()
  const { getLevelRewards, loading } = useLevelRewards();
  const [levelRewards, setLevelRewards] = useState<AdminLevelReward[]>([]);
  const [count, setCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filter, setFilter] = useState<{
    page: number;
    limit: number;
    level?: number;
    reward_type?: 'xp_bonus' | 'blockchain_token' | 'treasure_opportunity' | 'claw_coin' | 'store_discount' | 'custom_reward';
    active?: boolean;
  }>({
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
    level: searchParams.get('level') ? Number(searchParams.get('level')) : undefined,
    reward_type: searchParams.get('reward_type') as 'xp_bonus' | 'blockchain_token' | 'treasure_opportunity' | 'claw_coin' | 'store_discount' | 'custom_reward' | undefined,
    active: searchParams.get('active') ? searchParams.get('active') === 'true' : true,
  })
  const { page, limit, level, reward_type, active } = filter

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    let parsedValue = value;
    
    // Parsear el valor según el tipo de filtro
    if (name === 'page' || name === 'limit') {
      parsedValue = Number(value);
    } else if (name === 'level') {
      parsedValue = value ? Number(value) : undefined;
    } else if (name === 'active') {
      if (value === '' || value === undefined) {
        parsedValue = undefined;
      } else {
        parsedValue = value === 'true';
      }
    }
    
    setFilter({ ...filter, [name]: parsedValue })

    if (value && value !== '') searchParams.set(name, String(value))
    else searchParams.delete(name)
    setSearchParams(searchParams)
  }

  const onClearFilters = () => {
    setFilter({
      page: 1,
      limit: 10,
      level: undefined,
      reward_type: undefined,
      active: true,
    });
    setSearchParams(new URLSearchParams({ active: 'true' }));
  }

  const refreshLevelRewards = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchLevelRewards = async () => {
    try {
      const response = await getLevelRewards({ 
        limit, 
        page, 
        level, 
        reward_type, 
        active
      });
      setLevelRewards(response.levelRewards);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching level rewards:', error);
    }
  };

  useEffect(() => {
    fetchLevelRewards();
  }, [page, limit, level, reward_type, active, refreshTrigger])

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlLevel = searchParams.get('level');
    const urlRewardType = searchParams.get('reward_type');
    const urlActive = searchParams.get('active');

    if (urlPage || urlLimit || urlLevel || urlRewardType || urlActive) {
      setFilter({
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 10),
        level: urlLevel ? Number(urlLevel) : undefined,
        reward_type: urlRewardType as 'xp_bonus' | 'blockchain_token' | 'treasure_opportunity' | 'claw_coin' | 'store_discount' | 'custom_reward' | undefined,
        active: urlActive ? urlActive === 'true' : true
      });
    }
  }, []);

  const rewardTypesOptions = [
    { value: 'xp_bonus', label: 'XP' },
    { value: 'blockchain_token', label: 'Blockchain Token' },
    { value: 'treasure_opportunity', label: 'Oportunidad de Tesoro' },
    { value: 'claw_coin', label: 'Moneda Garra' },
    { value: 'store_discount', label: 'Descuento en Tienda' },
    { value: 'custom_reward', label: 'Premio Personalizado' },
  ];

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Premios por Nivel
        </Heading>

        <Flex w='full' alignItems='center' gap='5'>
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(ROUTES.LEVEL_REWARDS.create)}
            isDisabled={missingPermission(OlaggModule.LevelReward, PermissionEnum.CREATE)}
          >
            Agregar Premio por Nivel
          </Button>
        </Flex>

        {/* Inline filter */}
        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Level
            </Text>
            <Input
              placeholder="Buscar por nivel..."
              value={level || ''}
              onChange={(e) => onFilter({ name: 'level', value: e.target.value || undefined })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            />
          </Box>

          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Estado
            </Text>
            <Select
              value={active === undefined ? '' : String(active)}
              onChange={(e) => onFilter({ name: 'active', value: e.target.value === '' ? undefined : e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            >
              <option value="">Todos</option>
              <option value="true">Activa</option>
              <option value="false">No activa</option>
            </Select>
          </Box>

          <Box>
            <Text color="transparent" fontSize="sm" mb={1}>
              .
            </Text>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          levelRewards?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                        <Th py={'20px'} color="white">Nivel</Th>
                        <Th py={'20px'} color="white">Tipo de Premio</Th>
                        <Th py={'20px'} color="white">Configuración</Th>
                        <Th py={'20px'} color="white">Activo</Th>
                        <Th py={'20px'} color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {levelRewards?.map((reward, index) => (
                        <Tr key={index} color="white">
                          <Td py={'20px'}>{reward?.level}</Td>
                          <Td py={'20px'}>{rewardTypesOptions.find(option => option.value === reward?.rewardType)?.label}</Td>

                          <Td py={'20px'}>
                            {/* Render rewardConfig in a compact, readable way */}
                            {(() => {
                              const cfg = reward?.rewardConfig || {};
                              switch (reward.rewardType) {
                                case 'xp_bonus':
                                  return <Text>XP: {cfg.amount ?? '-'}</Text>;

                                case 'blockchain_token':
                                  return (
                                    <VStack align="start" spacing={1}>
                                      <Text>Network ID: {cfg.networkId ?? '-'}</Text>
                                      <Text>Token ID: {cfg.tokenId ?? cfg.token ?? '-'}</Text>
                                      <Text>Quantity: {cfg.quantity ?? cfg.amount ?? '-'}</Text>
                                    </VStack>
                                  );

                                case 'treasure_opportunity':
                                  return <Text>Oportunidades: {cfg.amount ?? '-'}</Text>;

                                case 'claw_coin':
                                  return (
                                    <VStack align="start" spacing={1}>
                                      <Text>Monedas: {cfg.amount ?? '-'}</Text>
                                      {cfg.expirationDays && (
                                        <Text>Expira en: {cfg.expirationDays} días</Text>
                                      )}
                                    </VStack>
                                  );

                                case 'store_discount':
                                  return (
                                    <VStack align="start" spacing={1}>
                                      <Text>Descuento: {cfg.discountPercentage ?? '-'}%</Text>
                                      <Text>Límite: {cfg.discountLimit ?? '-'}</Text>
                                      <Text>Expira en: {cfg.discountExpirationDays ?? '-'} días</Text>
                                    </VStack>
                                  );

                                case 'custom_reward':
                                  return (
                                    <HStack align="start" spacing={3}>
                                      {cfg.icon ? (
                                        <Image src={cfg.icon} alt="icon" boxSize="40px" objectFit="contain" borderRadius="md" />
                                      ) : null}
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="bold">{reward.title ?? '-'}</Text>
                                        {/* show translations count if available */}
                                        {reward.extraData && (reward.extraData.en || reward.extraData.pt) ? (
                                          <Text fontSize="sm" color="gray.300">Translations: {reward.extraData.en ? 'EN ' : ''}{reward.extraData.pt ? 'PT' : ''}</Text>
                                        ) : null}
                                      </VStack>
                                    </HStack>
                                  );

                                default:
                                  // Generic fallback: pretty print keys
                                  return (
                                    <VStack align="start" spacing={1}>
                                      {Object.keys(cfg).length === 0 ? (
                                        <Text color="gray.400">Sin configuración</Text>
                                      ) : (
                                        Object.entries(cfg).slice(0, 5).map(([k, v]) => (
                                          <Text key={k}><Text as="span" fontWeight="bold">{k}:</Text> {String(v)}</Text>
                                        ))
                                      )}
                                    </VStack>
                                  );
                              }
                            })()}
                          </Td>

                          <Td py={'20px'}>
                            <Badge colorScheme={reward.active ? 'green' : 'red'}>
                              {reward.active ? 'Visible' : 'Oculta'}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>
                            <Actions levelReward={reward} onLevelRewardDeleted={refreshLevelRewards} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay premios por nivel cargados</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />
      </Box>
    </Center>
  )
}

export default LevelRewardsList;